import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { Clock, Save, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MN = ["","Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];

export default function WorkHoursAdminSection({ getAuthHeaders }) {
  const [employees, setEmployees] = useState([]);
  const [allHours, setAllHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({
    personnel_number: "", month: String(new Date().getMonth() + 1),
    year: String(new Date().getFullYear()), regular_hours: "", overtime_hours: "0"
  });
  const years = Array.from({ length: 4 }, (_, i) => String(new Date().getFullYear() - i));

  const fetchData = useCallback(async () => {
    try {
      const [e, h] = await Promise.all([
        axios.get(API + '/admin/employees', { headers: getAuthHeaders() }),
        axios.get(API + '/admin/work-hours', { headers: getAuthHeaders() }),
      ]);
      setEmployees(e.data); setAllHours(h.data);
    } catch { toast.error("Fehler beim Laden"); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.personnel_number || !form.regular_hours) { toast.error("Bitte alle Pflichtfelder ausfüllen"); return; }
    setSaving(true);
    try {
      await axios.post(API + '/admin/work-hours', {
        personnel_number: form.personnel_number, month: parseInt(form.month), year: parseInt(form.year),
        regular_hours: parseFloat(form.regular_hours), overtime_hours: parseFloat(form.overtime_hours || 0),
      }, { headers: getAuthHeaders() });
      toast.success("Stunden gespeichert");
      setForm(f => ({ ...f, regular_hours: "", overtime_hours: "0" }));
      await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (pnr, year, month) => {
    if (!window.confirm("Stunden löschen?")) return;
    try {
      await axios.delete(API + '/admin/work-hours/' + pnr + '/' + year + '/' + month, { headers: getAuthHeaders() });
      toast.success("Gelöscht"); await fetchData();
    } catch { toast.error("Fehler"); }
  };

  const empHours = (pnr) => allHours.filter(h => h.personnel_number === pnr).sort((a, b) => b.year - a.year || b.month - a.month);
  const total = (parseFloat(form.regular_hours || 0) + parseFloat(form.overtime_hours || 0)).toFixed(1);

  if (loading) return <Skeleton className="h-64"/>;

  return (
    <div className="space-y-6">
      <Card className="border-blue-200 shadow-none">
        <CardHeader><div className="flex items-center gap-3"><Clock className="w-5 h-5 text-blue-600"/><CardTitle className="font-heading font-semibold text-xl text-slate-900">Stunden erfassen</CardTitle></div></CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label className="font-heading font-medium text-xs uppercase tracking-wider text-slate-500">Mitarbeiter *</Label>
                <Select value={form.personnel_number} onValueChange={v => setForm(f => ({ ...f, personnel_number: v }))}>
                  <SelectTrigger className="h-11 border-slate-300"><SelectValue placeholder="Mitarbeiter wählen"/></SelectTrigger>
                  <SelectContent className="z-[60]">{employees.filter(e => e.role !== "admin").map(emp => (
                    <SelectItem key={emp.personnel_number} value={emp.personnel_number}>{emp.personnel_number} — {emp.first_name} {emp.last_name}</SelectItem>
                  ))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-heading font-medium text-xs uppercase tracking-wider text-slate-500">Monat *</Label>
                <Select value={form.month} onValueChange={v => setForm(f => ({ ...f, month: v }))}>
                  <SelectTrigger className="h-11 border-slate-300"><SelectValue/></SelectTrigger>
                  <SelectContent className="z-[60]">{MN.slice(1).map((n, i) => <SelectItem key={i+1} value={String(i+1)}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-heading font-medium text-xs uppercase tracking-wider text-slate-500">Jahr *</Label>
                <Select value={form.year} onValueChange={v => setForm(f => ({ ...f, year: v }))}>
                  <SelectTrigger className="h-11 border-slate-300"><SelectValue/></SelectTrigger>
                  <SelectContent className="z-[60]">{years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-heading font-medium text-xs uppercase tracking-wider text-slate-500">Reguläre Stunden *</Label>
                <Input type="number" step="0.5" min="0" max="300" placeholder="z.B. 168" value={form.regular_hours}
                  onChange={e => setForm(f => ({ ...f, regular_hours: e.target.value }))} className="h-11 border-slate-300" required/>
              </div>
              <div className="space-y-2">
                <Label className="font-heading font-medium text-xs uppercase tracking-wider text-slate-500">Überstunden</Label>
                <Input type="number" step="0.5" min="0" max="200" placeholder="z.B. 12" value={form.overtime_hours}
                  onChange={e => setForm(f => ({ ...f, overtime_hours: e.target.value }))} className="h-11 border-slate-300"/>
              </div>
            </div>
            {form.regular_hours && <p className="text-sm text-slate-500">Gesamt: <strong>{total} Std.</strong></p>}
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white h-11 rounded-md font-heading uppercase tracking-wide text-xs">
              <Save className="w-4 h-4 mr-2"/>{saving ? 'Wird gespeichert...' : 'Stunden speichern'}
            </Button>
          </form>
        </CardContent>
      </Card>
      <div>
        <h3 className="font-heading font-semibold text-lg text-slate-900 mb-3">Erfasste Stunden</h3>
        {employees.filter(e => e.role !== "admin").map(emp => {
          const hours = empHours(emp.personnel_number);
          if (hours.length === 0) return null;
          const isExp = expanded === emp.personnel_number;
          return (
            <Card key={emp.personnel_number} className="border-slate-200 shadow-none mb-2">
              <CardContent className="p-0">
                <button className="w-full p-4 flex items-center justify-between hover:bg-slate-50 rounded-lg transition-colors"
                  onClick={() => setExpanded(isExp ? null : emp.personnel_number)}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-600">{emp.first_name?.[0]}{emp.last_name?.[0]}</div>
                    <div className="text-left">
                      <p className="font-heading font-semibold text-sm">{emp.first_name} {emp.last_name}</p>
                      <p className="text-xs text-slate-400">{emp.personnel_number} · {hours.length} Monat{hours.length !== 1 ? 'e' : ''}</p>
                    </div>
                  </div>
                  {isExp ? <ChevronDown className="w-4 h-4 text-slate-400"/> : <ChevronRight className="w-4 h-4 text-slate-400"/>}
                </button>
                {isExp && (
                  <div className="px-4 pb-4 space-y-2">
                    {hours.map(h => (
                      <div key={h.month+'-'+h.year} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <span className="font-heading font-medium text-sm">{MN[h.month]} {h.year}</span>
                          <p className="text-xs text-slate-500 mt-0.5">{parseFloat(h.regular_hours).toFixed(1)} reg. · {parseFloat(h.overtime_hours).toFixed(1)} ÜS · <strong>{parseFloat(h.total_hours).toFixed(1)} ges.</strong></p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(emp.personnel_number, h.year, h.month)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        {allHours.length === 0 && <div className="text-center py-8"><Clock className="w-10 h-10 text-slate-200 mx-auto"/><p className="mt-2 text-slate-400 text-sm">Noch keine Stunden erfasst</p></div>}
      </div>
    </div>
  );
}
